import {
  differenceInMonths,
  format,
  formatDistanceStrict,
  intlFormat,
  isValid,
  type Locale,
  parseISO,
} from 'date-fns';
import { enUS, es, pt } from 'date-fns/locale';

import type { AppConfigDtOverride } from '#/config/types/AppConfig';
import { I18nLocale } from '#/i18n/types/I18nLocale';

import { IDEALIST_TIME_ZONE } from './constants/general/idealistTimeZone';

export const DATE_FNS_LOCALE_BY_LOCALE = {
  [I18nLocale.EN]: enUS,
  [I18nLocale.ES]: es,
  [I18nLocale.PT]: pt,
} satisfies Record<I18nLocale, Locale>;

const DATE_ONLY_STRING_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function getDate(date: string | number | Date) {
  if (typeof date !== 'string') return date;

  if (DATE_ONLY_STRING_PATTERN.test(date)) {
    // Normalize date-only strings to a stable midday instant so formatting
    // them in western timezones does not slip to the previous calendar day.
    return parseISO(`${date}T12:00:00.000Z`);
  }

  return parseISO(date);
}

function getDateFnsLocale(locale: I18nLocale) {
  return DATE_FNS_LOCALE_BY_LOCALE[locale];
}

function getLocaleCode(locale: I18nLocale) {
  return getDateFnsLocale(locale).code;
}

function normalizeDateInput(date: string | number | Date) {
  return getDate(date);
}

function formatLocalizedDate({
  date,
  locale,
  pattern,
}: {
  date: string | number | Date;
  locale: I18nLocale;
  pattern: string;
}) {
  return format(normalizeDateInput(date), pattern, {
    locale: getDateFnsLocale(locale),
  });
}

function formatTime(
  date: string | number | Date,
  localeCode: string,
  params: Parameters<typeof Intl.DateTimeFormat>[1] = Object.freeze({}),
) {
  const { timeZone = undefined, ...spreadParams } = params;
  return new Date(date).toLocaleTimeString(localeCode, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timeZone ?? undefined,
    ...spreadParams,
  });
}

function formatLocalizedTime({
  date,
  locale,
  timezone,
  displayTimezone,
}: {
  date: string | number | Date;
  locale: I18nLocale;
  timezone?: string | null;
  displayTimezone?: boolean;
}) {
  const options: Parameters<typeof Intl.DateTimeFormat>[1] = {
    timeZoneName: displayTimezone ? 'short' : undefined,
    timeZone: timezone ?? undefined,
  };

  return formatTime(date, getLocaleCode(locale), options);
}

function formatLocalizedCalendarDate({
  date,
  locale,
  timezone,
  useShortMonth,
}: {
  date: string | number | Date;
  locale: I18nLocale;
  timezone?: string | null;
  useShortMonth?: boolean;
}) {
  return intlFormat(
    normalizeDateInput(date),
    {
      year: 'numeric',
      month: useShortMonth ? 'short' : 'long',
      day: 'numeric',
      timeZone: timezone ?? undefined,
    },
    {
      locale: getLocaleCode(locale),
    },
  );
}

const SHORTER_DATE_FORMAT = {
  en: 'M/d/yyyy',
  es: 'd/M/yyyy',
  pt: 'd/M/yyyy',
} satisfies Record<I18nLocale, string>;

export function shorterDate({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return formatLocalizedDate({
    date,
    locale,
    pattern: SHORTER_DATE_FORMAT[locale],
  });
}

const SHORTEST_DATE_FORMAT = {
  en: 'M/d',
  es: 'd/M',
  pt: 'd/M',
};

export function shortestDate({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return formatLocalizedDate({
    date,
    locale,
    pattern: SHORTEST_DATE_FORMAT[locale],
  });
}

const SHORTEST_DATE_WITH_YEAR_FORMAT = {
  en: 'M/d/yy',
  es: 'd/M/yy',
  pt: 'd/M/yy',
};

export function shortestDateWithYear({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return formatLocalizedDate({
    date,
    locale,
    pattern: SHORTEST_DATE_WITH_YEAR_FORMAT[locale],
  });
}

export function shorterTime({
  date,
  locale,
  timezone,
  displayTimezone,
}: {
  date: string | number | Date;
  locale: I18nLocale;
  timezone?: string | null;
  displayTimezone?: boolean;
}) {
  return formatLocalizedTime({
    date,
    locale,
    timezone,
    displayTimezone,
  });
}

const DATETIME_FORMAT = {
  en: 'MM/dd/yyyy  hh:mma',
  es: 'dd/MM/yyyy HH:mm',
  pt: 'dd/MM/yyyy HH:mm',
};

export function dateTime({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return formatLocalizedDate({
    date,
    locale,
    pattern: DATETIME_FORMAT[locale],
  });
}

const SHORT_DATETIME_FORMAT_BY_LOCALE = {
  en: 'M/d/yy h:mma',
  es: 'd/M/yy HH:mm',
  pt: 'd/M/yy HH:mm',
} satisfies Record<I18nLocale, string>;

export function shortDateTime({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return formatLocalizedDate({
    date,
    locale,
    pattern: SHORT_DATETIME_FORMAT_BY_LOCALE[locale],
  });
}

export function humanDate({
  date,
  locale,
  timezone,
  opts = {},
}: {
  date: string | number | Date;
  locale: I18nLocale;
  timezone?: string | null;
  opts?: {
    shortMonth?: boolean;
  };
}) {
  const parsedDate = normalizeDateInput(date);
  if (!isValid(parsedDate)) return 'Invalid Date';

  return formatLocalizedCalendarDate({
    date: parsedDate,
    locale,
    timezone,
    useShortMonth: opts?.shortMonth,
  });
}

const HUMAN_DATETIME_FORMAT = {
  en: 'MMM d, yyyy h:mm a',
  es: 'MMM d, yyyy H:mm',
  pt: 'MMM d, yyyy H:mm',
};

export function humanDateTime({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return formatLocalizedDate({
    date,
    locale,
    pattern: HUMAN_DATETIME_FORMAT[locale],
  });
}

export function monthAndYear({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return formatLocalizedDate({
    date,
    locale,
    pattern: 'MMMM yyyy',
  });
}

export function dayAndMonth({
  date,
  locale,
  longMonth,
}: {
  date: string | number | Date;
  locale: I18nLocale;
  longMonth?: boolean;
}) {
  return formatLocalizedDate({
    date,
    locale,
    pattern: longMonth ? 'MMMM d' : 'MMM d',
  });
}

export function dowDayAndMonth({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return formatLocalizedDate({
    date,
    locale,
    pattern: 'cccc, MMMM d',
  });
}

export function dayNumber({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return formatLocalizedDate({
    date,
    locale,
    pattern: 'd',
  });
}

export function shortMonth({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return formatLocalizedDate({
    date,
    locale,
    pattern: 'MMM',
  }).slice(0, 3);
}

export function yearOnly({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return formatLocalizedDate({
    date,
    locale,
    pattern: 'yyyy',
  });
}

export function shortMonthAndYear({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return formatLocalizedDate({
    date,
    locale,
    pattern: 'MMM yyyy',
  });
}

function resolveCurrentDateTime(dtOverride: AppConfigDtOverride) {
  if (dtOverride) {
    return parseISO(dtOverride);
  }

  return new Date();
}

export function now({ dtOverride }: { dtOverride: AppConfigDtOverride }) {
  return resolveCurrentDateTime(dtOverride);
}

export function daysInMs(days: number) {
  return 1000 * 60 * 60 * 24 * days;
}

export function distanceInWordsToNow({
  date,
  locale,
  dtOverride,
}: {
  date: string | number | Date;
  locale: I18nLocale;
  dtOverride: AppConfigDtOverride;
}) {
  return formatDistanceStrict(
    normalizeDateInput(date),
    resolveCurrentDateTime(dtOverride),
    {
      addSuffix: true,
      locale: getDateFnsLocale(locale),
      roundingMethod: 'floor',
    },
  );
}

function formatAbbreviatedDateDistancePart(amount: number, index: number) {
  if (amount === 0) {
    return null;
  }

  return `${amount} ${index === 0 ? 'yr' : 'mo'}${amount > 1 ? 's' : ''}`;
}

export function distanceInYearsAndMonthsAbbr({
  start,
  end,
}: {
  start: string | number | Date;
  end: string | number | Date;
}) {
  const distance = differenceInMonths(
    normalizeDateInput(start),
    normalizeDateInput(end),
  );

  if (!distance) {
    return null;
  }

  const years = Math.floor(distance / 12);
  const months = distance - years * 12;
  return [years, months]
    .map((amt, index) => formatAbbreviatedDateDistancePart(amt, index))
    .filter((amt) => amt)
    .join(', ');
}

function adjustDateByTimezoneOffset(date: Date, direction: 1 | -1) {
  return new Date(
    date.getTime() + date.getTimezoneOffset() * 60000 * direction,
  );
}

// Converts a date or date string to GMT timezone
export function toGMTDate(date: string | Date) {
  return adjustDateByTimezoneOffset(new Date(date), 1);
}

export function stripTimezone(dateString: string) {
  return dateString.replace('Z', '');
}

// Take a timezone-aware date and return the user's date / time (not timezone-aware)
// as a string
export function formatLocalDate(d: Date): string {
  const convertedDate = adjustDateByTimezoneOffset(d, -1);
  return stripTimezone(convertedDate.toISOString());
}

function getIdealistDateTimeParts(d: Date | string) {
  const formatterOptions = {
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: IDEALIST_TIME_ZONE,
  } satisfies Intl.DateTimeFormatOptions;

  const formattedDateParts = new Intl.DateTimeFormat(
    'en-us',
    formatterOptions,
  ).formatToParts(normalizeDateInput(d));

  const dateTimeParts = formattedDateParts.reduce<{
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
  } | null>((parts, part) => {
    if (
      part.type !== 'year' &&
      part.type !== 'month' &&
      part.type !== 'day' &&
      part.type !== 'hour' &&
      part.type !== 'minute' &&
      part.type !== 'second'
    ) {
      return parts;
    }

    return {
      ...(parts ?? {
        year: 0,
        month: 0,
        day: 0,
        hour: 0,
        minute: 0,
        second: 0,
      }),
      [part.type]: parseInt(part.value, 10),
    };
  }, null);

  if (!dateTimeParts) {
    throw new Error('Unable to parse Idealist datetime parts');
  }

  return dateTimeParts;
}

export function formatIdealistDateTime(d: Date | string): Date {
  // XXX alternative implementation of this function could be this, but would need QA:
  // export const formatIdealistDateTime = (d: Date | string): Date => {
  //   new Date(d.toLocaleString('en-US', { timeZone: IDEALIST_TIME_ZONE }));
  const { year, month, day, hour, minute, second } =
    getIdealistDateTimeParts(d);

  return new Date(year, month - 1, day, hour === 24 ? 0 : hour, minute, second);
}

const TIMEZONE_OFFSET_PATTERN = /(Z|[+-]\d{2}(?::?\d{2})?)$/;

function parseTimezoneOffsetMinutes(offsetToken: string) {
  if (offsetToken === 'Z') {
    return 0;
  }

  const sign = offsetToken.startsWith('-') ? -1 : 1;
  const normalizedOffset = offsetToken.slice(1).replace(':', '');
  const hours = parseInt(normalizedOffset.slice(0, 2), 10);
  const minutes =
    normalizedOffset.length > 2
      ? parseInt(normalizedOffset.slice(2, 4), 10)
      : 0;

  return sign * (hours * 60 + minutes);
}

function getDateOffsetMinutes(date: string | Date) {
  if (date instanceof Date) {
    return -date.getTimezoneOffset();
  }

  const offsetMatch = TIMEZONE_OFFSET_PATTERN.exec(date);
  return offsetMatch ? parseTimezoneOffsetMinutes(offsetMatch[1]) : 0;
}

function shiftDateByOffset(date: Date, offsetMinutes: number) {
  return new Date(date.getTime() + offsetMinutes * 60000);
}

function getShiftedCalendarDayStart(date: Date, offsetMinutes: number) {
  const shiftedDate = shiftDateByOffset(date, offsetMinutes);

  return Date.UTC(
    shiftedDate.getUTCFullYear(),
    shiftedDate.getUTCMonth(),
    shiftedDate.getUTCDate(),
  );
}

function getComparisonDayStart({
  date,
  dtOverride,
}: {
  date: string | Date;
  dtOverride: AppConfigDtOverride;
}) {
  return getShiftedCalendarDayStart(
    resolveCurrentDateTime(dtOverride),
    getDateOffsetMinutes(date),
  );
}

function getTargetDayStart(date: string | Date) {
  return getShiftedCalendarDayStart(
    new Date(normalizeDateInput(date)),
    getDateOffsetMinutes(date),
  );
}

export function dateIsToday({
  date,
  dtOverride,
}: {
  date: string | Date;
  dtOverride: AppConfigDtOverride;
}) {
  return (
    getComparisonDayStart({ date, dtOverride }) === getTargetDayStart(date)
  );
}

export function dateIsUpcoming({
  date,
  dtOverride,
}: {
  date: string | Date;
  dtOverride: AppConfigDtOverride;
}) {
  return getTargetDayStart(date) >= getComparisonDayStart({ date, dtOverride });
}

export function dateIsPast({
  date,
  dtOverride,
}: {
  date: string | Date;
  dtOverride: AppConfigDtOverride;
}) {
  return !dateIsUpcoming({ date, dtOverride });
}

export function getCurrentYear({
  dtOverride,
}: {
  dtOverride: AppConfigDtOverride;
}) {
  return resolveCurrentDateTime(dtOverride).getFullYear();
}
